import { useEffect, useState } from "react";
import { MessageSquareQuote, Star, Trash2, Send, Loader2, Check, Hourglass, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    fetchMyTestimonial, submitTestimonial, deleteMyTestimonial, formatApiError,
} from "@/lib/api";

/**
 * Inline card on the Profile page where users submit / edit / delete their
 * own testimonial. One testimonial per user — resubmits overwrite and go
 * back to pending. Status is surfaced ("Pending review" / "Published" /
 * "Not yet published") so users know exactly where their quote stands.
 */

const MIN_QUOTE = 40;
const MAX_QUOTE = 400;

function StatusPill({ status }) {
    if (status === "approved") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--vm-sage)]/15 text-[var(--vm-sage)] text-[10px] uppercase tracking-wide font-semibold" data-testid="testimonial-status-approved">
                <Check className="w-3 h-3" strokeWidth={2.5} />
                Published on About page
            </span>
        );
    }
    if (status === "rejected") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--vm-secondary)]/15 text-[var(--vm-secondary)] text-[10px] uppercase tracking-wide font-semibold" data-testid="testimonial-status-rejected">
                <X className="w-3 h-3" strokeWidth={2.5} />
                Not published
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--vm-accent)]/15 text-[var(--vm-accent)] text-[10px] uppercase tracking-wide font-semibold" data-testid="testimonial-status-pending">
            <Hourglass className="w-3 h-3" strokeWidth={2} />
            Pending review
        </span>
    );
}

function StarPicker({ value, onChange }) {
    return (
        <div className="flex items-center gap-1" data-testid="testimonial-rating-picker">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onClick={() => onChange(value === n ? null : n)}
                    className="p-1 hover:scale-110 transition-transform"
                    data-testid={`testimonial-rating-${n}`}
                    aria-label={`Rate ${n} out of 5 stars`}
                >
                    <Star
                        className={`w-5 h-5 ${value && n <= value ? "fill-[var(--vm-accent)] text-[var(--vm-accent)]" : "text-[var(--vm-border)]"}`}
                        strokeWidth={1.5}
                    />
                </button>
            ))}
            {value && (
                <button
                    type="button"
                    onClick={() => onChange(null)}
                    className="ml-2 text-xs text-[var(--vm-text-muted)] hover:text-[var(--vm-text)]"
                >
                    Clear
                </button>
            )}
        </div>
    );
}

export default function ProfileTestimonialCard() {
    const [loading, setLoading] = useState(true);
    const [existing, setExisting] = useState(null);
    const [quote, setQuote] = useState("");
    const [location, setLocation] = useState("");
    const [rating, setRating] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let ignore = false;
        fetchMyTestimonial()
            .then((doc) => {
                if (ignore) return;
                setExisting(doc);
                setQuote(doc?.quote || "");
                setLocation(doc?.location || "");
                setRating(doc?.rating ?? null);
            })
            .catch(() => { /* ignore 401 for not-loaded users */ })
            .finally(() => { if (!ignore) setLoading(false); });
        return () => { ignore = true; };
    }, []);

    const trimmedQuote = quote.trim();
    const quoteTooShort = trimmedQuote.length < MIN_QUOTE;
    const quoteTooLong = trimmedQuote.length > MAX_QUOTE;
    const canSubmit = !quoteTooShort && !quoteTooLong && !busy;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        try {
            const saved = await submitTestimonial({
                quote: trimmedQuote,
                location: location.trim() || null,
                rating: rating || null,
            });
            setExisting(saved);
            toast.success(existing
                ? "Thanks — your testimonial was updated and is awaiting review."
                : "Thank you! Your testimonial will appear on the About page once reviewed.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Delete your testimonial?")) return;
        setBusy(true);
        try {
            await deleteMyTestimonial();
            setExisting(null);
            setQuote("");
            setLocation("");
            setRating(null);
            toast.success("Testimonial removed.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (loading) return null;

    return (
        <section className="journal-card p-6" data-testid="profile-testimonial-card">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--vm-accent)]/12 flex items-center justify-center shrink-0">
                        <MessageSquareQuote className="w-4 h-4 text-[var(--vm-accent)]" strokeWidth={1.5} />
                    </div>
                    <div>
                        <h2 className="font-serif-display text-2xl text-[var(--vm-text)] leading-tight">
                            Share your garden story
                        </h2>
                        <p className="text-xs text-[var(--vm-text-muted)] mt-1">
                            Tell new gardeners how rooteed has helped your season. We'll review before publishing on the public About page.
                        </p>
                    </div>
                </div>
                {existing && <StatusPill status={existing.status} />}
            </div>

            <form onSubmit={submit} className="space-y-4">
                <div>
                    <Label htmlFor="testimonial-quote" className="text-xs uppercase tracking-wide text-[var(--vm-sage)]">
                        Your words
                    </Label>
                    <Textarea
                        id="testimonial-quote"
                        data-testid="testimonial-quote-input"
                        rows={4}
                        placeholder="What's rooteed actually changed for your garden this season?"
                        value={quote}
                        onChange={(e) => setQuote(e.target.value)}
                        className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)] resize-none"
                        maxLength={MAX_QUOTE + 50}
                    />
                    <div className="mt-1 flex items-center justify-between text-[11px]">
                        <span className={quoteTooShort ? "text-[var(--vm-secondary)]" : "text-[var(--vm-text-muted)]"}>
                            {quoteTooShort
                                ? `Add at least ${MIN_QUOTE - trimmedQuote.length} more characters`
                                : "A sentence or two is perfect."}
                        </span>
                        <span className={quoteTooLong ? "text-[var(--vm-secondary)]" : "text-[var(--vm-text-muted)]"}>
                            {trimmedQuote.length} / {MAX_QUOTE}
                        </span>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="testimonial-location" className="text-xs uppercase tracking-wide text-[var(--vm-sage)]">
                            Garden location <span className="opacity-70 normal-case">(optional)</span>
                        </Label>
                        <Input
                            id="testimonial-location"
                            data-testid="testimonial-location-input"
                            placeholder="e.g. Galway allotment"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            maxLength={80}
                            className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-[var(--vm-sage)]">
                            Rating <span className="opacity-70 normal-case">(optional)</span>
                        </Label>
                        <div className="mt-2">
                            <StarPicker value={rating} onChange={setRating} />
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-1">
                    <Button
                        type="submit"
                        disabled={!canSubmit}
                        data-testid="testimonial-submit-btn"
                        className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                    >
                        {busy
                            ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Saving…</>)
                            : existing
                                ? (<><Send className="w-3.5 h-3.5 mr-2" />Resubmit for review</>)
                                : (<><Send className="w-3.5 h-3.5 mr-2" />Submit testimonial</>)}
                    </Button>
                    {existing && (
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={remove}
                            disabled={busy}
                            data-testid="testimonial-delete-btn"
                            className="rounded-full text-[var(--vm-text-muted)] hover:text-[var(--vm-secondary)]"
                        >
                            <Trash2 className="w-3.5 h-3.5 mr-2" />
                            Delete
                        </Button>
                    )}
                </div>
            </form>
        </section>
    );
}
